/**
 * Hospital staff: create the twelve kinds of account, and manage them.
 *
 * Dashboard 2 of the upgrade. Each role carries its own quota — how many were
 * created and how many remain — set by the Super Admin per hospital; the grid
 * above the roster says it before anybody presses create, and the server
 * enforces it under a row lock whatever the screen says.
 *
 * The screen the whole upgrade exists to make possible. It is deliberately a
 * sibling of InstitutionMembers rather than a rewrite of it — that screen still
 * serves a lab centre or a pharmacy adding a doctor, and it should not grow a
 * privileges matrix it has no use for.
 *
 * Two details are load-bearing rather than cosmetic:
 *
 * The role picker offers exactly `CREATABLE_MEMBER_ROLES`, which the parity
 * test pins against the server's own list. The server refuses an unrecognised
 * role outright — it no longer coerces to `doctor` — so an option here that the
 * server does not know is a 400 the user cannot act on, and an option missing
 * here is a role nobody can create.
 *
 * The privileges matrix appears only for a co-admin, because that is the only
 * role whose bundle the server stores. Ticking nothing is a deliberate
 * "nothing", not "use the default" — the form says so, because those two states
 * are indistinguishable in a checkbox grid and the difference is whether the
 * deputy can do their job.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  Input,
  Modal,
  Panel,
  Select,
  Spinner,
} from '../../components/common';
import { HeroBand, StatTile3D, Tile3D } from '../../components/common/Tile3D';
import { useAsync } from '../../hooks/useAsync';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';
import { institutionApi, roomsApi } from '../../api/endpoints';
import {
  CREATABLE_MEMBER_ROLES,
  MEMBER_ROLE_LABELS,
  PERMISSIONS,
  PERMISSION_GROUPS,
  PERMISSION_LABELS,
  SPEC_MEMBER_ROLES,
  mayManageStaffRole,
} from '../../domain/rbac';
import { useAuth } from '../../context/AuthContext';
import { EditAccountModal, HospitalBrandingPanel, ResetPasswordDialog } from './StaffAccountTools';
import { PackageCard } from './PackageCard';
import { PERMISSION_CONTROL, roleOffered } from '../../domain/serviceControl';

/**
 * The glyph, tone and one-line job description for each account type.
 *
 * The hint is the part that matters. "مدير إداري" tells a hospital owner what
 * the role is called; the hint tells them whether it is the one they want —
 * which is the actual question in front of them.
 */
const ROLE_ICON = {
  co_admin: 'shield-check',
  receptionist: 'clipboard-list',
  operations_manager: 'layout-dashboard',
  secretary: 'monitor-check',
  doctor: 'stethoscope',
  staff: 'users',
  contracts_manager: 'shield-plus',
  accounts_manager: 'hand-coins',
  rooms_manager: 'bed-double',
  nurse: 'syringe',
  lab_technician: 'flask-conical',
  radiologist: 'scan-line',
  pharmacist: 'pill',
};

const ROLE_TILE_TONE = {
  co_admin: 'purple',
  receptionist: 'cyan',
  operations_manager: 'emerald',
  secretary: 'amber',
  doctor: 'rose',
  staff: 'slate',
  contracts_manager: 'purple',
  accounts_manager: 'emerald',
  rooms_manager: 'rose',
  nurse: 'teal',
  lab_technician: 'teal',
  radiologist: 'blue',
  pharmacist: 'orange',
};

const ROLE_HINT = {
  co_admin: 'يدير المنشأة والشعار وحسابات الموظفين',
  receptionist: 'يسجّل المرضى ويصدر الإيصالات ويوجّه',
  operations_manager: 'يقبل المرضى ويدير جدول العيادات',
  secretary: 'يتابع طابور طبيب واحد',
  doctor: 'يكشف ويكتب الروشتات',
  staff: 'موظف عام بلا صلاحيات',
  contracts_manager: 'التعاقدات والموافقات المالية والتغطية',
  accounts_manager: 'يسعّر ويقفل الورديات ويرسل المستحقات',
  rooms_manager: 'العمليات والعناية والأقسام',
  nurse: 'يتابع مرضى غرفه',
  lab_technician: 'يستقبل التحاليل ويعتمد نتائجها',
  radiologist: 'يستقبل الأشعة ويكتب التقرير',
  pharmacist: 'يصرف الأدوية',
};

/** Which accent each role's badge carries, so a roster is scannable. */
const ROLE_TONE = {
  co_admin: 'border-purple-500/30 bg-purple-500/10 text-purple-300',
  receptionist: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
  operations_manager: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  secretary: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  doctor: 'border-rose-500/30 bg-rose-500/10 text-rose-300',
  staff: 'border-slate-500/30 bg-slate-500/10 text-slate-300',
  manager: 'border-teal-500/30 bg-teal-500/10 text-teal-300',
  contracts_manager: 'border-purple-500/30 bg-purple-500/10 text-purple-200',
  accounts_manager: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200',
  rooms_manager: 'border-rose-500/30 bg-rose-500/10 text-rose-200',
  nurse: 'border-teal-500/30 bg-teal-500/10 text-teal-200',
  lab_technician: 'border-teal-500/30 bg-teal-500/10 text-teal-200',
  radiologist: 'border-blue-500/30 bg-blue-500/10 text-blue-200',
  pharmacist: 'border-orange-500/30 bg-orange-500/10 text-orange-200',
};

const emptyForm = () => ({
  memberRole: 'receptionist',
  name: '',
  username: '',
  password: '',
  email: '',
  phone: '',
  servesDoctorId: '',
  permissions: null,
  title: '',
  priceExam: '',
  priceFollowup: '',
  assignedRoomIds: [],
});

/**
 * Created and remaining, per role.
 *
 * The requirement asks that the hospital admin see, for each of the twelve
 * roles, how many accounts were created and how many remain before pressing
 * create — so the quota is a grid of the roles, not one number. A role the
 * Super Admin set no cap for is bounded only by the hospital's overall total,
 * and the tile says so rather than showing an invented limit.
 */
function QuotaGrid({ quota, platform }) {
  if (!quota) return null;
  return (
    <Panel className="mb-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-bold text-white">
          <Icon name="bar-chart-2" size={16} className="text-cyan-300" /> الحسابات حسب الدور
        </h2>
        {platform ? (
          <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-200" icon="crown">
            المدير العام غير مقيّد بالحدود
          </Badge>
        ) : (
          <Badge className="border-white/10 bg-white/5 text-slate-300">
            الإجمالي {quota.overall.used} من {quota.overall.max} · متبقٍ {quota.overall.remaining}
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {SPEC_MEMBER_ROLES.map((role) => {
          const row = quota.byRole?.[role] || { used: 0, max: null, remaining: 0 };
          const full = !platform && row.remaining <= 0;
          return (
            <div
              key={role}
              data-quota-role={role}
              className={`flex items-center gap-2 rounded-xl border p-2.5 ${
                full ? 'border-red-500/30 bg-red-500/[0.07]' : 'border-white/5 bg-white/[0.03]'
              }`}
            >
              <span className={`tile3d__face tone-${ROLE_TILE_TONE[role] || 'cyan'} !h-9 !w-9 shrink-0`} aria-hidden="true">
                <Icon name={ROLE_ICON[role] || 'user'} size={16} className="tile3d__icon" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-bold text-white">{MEMBER_ROLE_LABELS[role]}</p>
                <p className="text-[11px] text-slate-400">
                  أُنشئ {row.used}
                  {row.max !== null ? ` من ${row.max}` : ''} ·{' '}
                  <span className={full ? 'font-bold text-red-300' : 'text-emerald-300'}>
                    {platform ? 'بلا حد' : `متبقٍ ${row.remaining}`}
                  </span>
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

export function HospitalStaff() {
  const { success, error: toastError } = useNotifications();
  const { can, actingAsPlatformAdmin, control, serviceShown } = usePermissions();
  const { role } = useAuth();

  const members = useAsync(() => institutionApi.members(), []);
  const institution = useAsync(() => institutionApi.mine(), []);
  // Rooms, for a nurse's assignment. Refused for a desk without the rooms
  // capability, in which case the nurse simply covers every room.
  const rooms = useAsync(() => roomsApi.list().catch(() => []), []);

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(null);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);

  // Memoised rather than `members.data || []` inline: a fresh [] on every
  // render would change the identity `doctors` depends on, and re-run that memo
  // for nothing on every keystroke in the form.
  const rows = useMemo(() => members.data || [], [members.data]);

  /** Secretaries are scoped to a doctor, so the picker needs the roster's doctors. */
  const doctors = useMemo(
    () => rows.filter((m) => m.memberRole === 'doctor' && m.active),
    [rows],
  );

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = useCallback(async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Only a co-admin carries a bundle; the server ignores it for anyone
      // else, but sending it would be a lie about what was asked for.
      if (payload.memberRole !== 'co_admin') delete payload.permissions;
      if (payload.memberRole !== 'secretary') delete payload.servesDoctorId;
      if (payload.memberRole !== 'doctor') {
        delete payload.title;
        delete payload.priceExam;
        delete payload.priceFollowup;
      }
      // Rooms travel only with a nurse; none ticked means every room.
      if (payload.memberRole !== 'nurse' || !payload.assignedRoomIds?.length) delete payload.assignedRoomIds;

      const created = await institutionApi.addMember(payload);
      success(`تم إنشاء حساب ${created.user?.name || ''}`);
      setForm(null);
      members.reload();
      institution.reload();
    } catch (err) {
      // The server's Arabic message is the useful one — quota full, username
      // taken, doctor not ours — so it is surfaced verbatim.
      toastError(err.message);
    } finally {
      setSaving(false);
    }
  }, [form, members, institution, success, toastError]);

  const suspend = useCallback(async () => {
    const member = confirmSuspend;
    setConfirmSuspend(null);
    try {
      await institutionApi.removeMember(member.id);
      success('تم إيقاف الحساب');
      members.reload();
    } catch (err) {
      toastError(err.message);
    }
  }, [confirmSuspend, members, success, toastError]);

  const reactivate = useCallback(
    async (member) => {
      try {
        await institutionApi.reactivateMember(member.id);
        success(`أُعيد تفعيل حساب ${member.user?.name || ''}`);
        members.reload();
        institution.reload();
      } catch (err) {
        toastError(err.message);
      }
    },
    [members, institution, success, toastError],
  );

  if (members.error) return <ErrorState error={members.error} onRetry={members.reload} />;
  // Only the first load shows the loader: a reload after an edit keeps the
  // roster on screen instead of flashing the whole page away.
  if (!members.data) return <Spinner label="جارٍ تحميل الفريق" />;

  const used = rows.filter((m) => m.active).length;
  const quota = institution.data?.maxSubAccounts ?? null;
  const mayManage = can(PERMISSIONS.HOSPITAL_STAFF_MANAGE);
  const mayBrand = can(PERMISSIONS.HOSPITAL_SETTINGS_MANAGE);
  // The platform admin working inside a hospital acts with the owner's reach.
  const actorRole = actingAsPlatformAdmin ? 'admin' : role;
  const mayTouch = (member) => mayManage && member.memberRole !== 'manager' && mayManageStaffRole(actorRole, member.memberRole);
  // Creating a peer administrative manager is the owner's call.
  // …and only the roles the package carries, on a desk that is open
  // (the central control; the server refuses the rest anyway).
  const creatableRoles = CREATABLE_MEMBER_ROLES.filter((r) => mayManageStaffRole(actorRole, r) && roleOffered(control, r));
  const roomList = Array.isArray(rooms.data) ? rooms.data : [];
  const roomName = (id) => roomList.find((r) => String(r.id) === String(id))?.name || `#${id}`;

  return (
    <div>
      <HeroBand
        icon="users"
        tone="purple"
        title="فريق المنشأة"
        subtitle="أنشئ حسابات موظفيك واختر ما يستطيع كل واحد منهم فعله."
      >
        {mayManage && (
          <Button onClick={() => setForm(emptyForm())} icon="user-plus">
            حساب جديد
          </Button>
        )}
      </HeroBand>

      <div className="mb-6 grid grid-cols-3 gap-3 sm:gap-4">
        <StatTile3D label="حسابات نشطة" value={used} icon="users" tone="cyan" delay={0} />
        <StatTile3D
          label="الحد الأقصى"
          value={quota ?? '—'}
          hint={quota !== null ? `${Math.max(0, quota - used)} متاح` : undefined}
          icon="shield-check"
          tone={quota !== null && used >= quota ? 'red' : 'emerald'}
          delay={1}
        />
        <StatTile3D
          label="أطباء"
          value={doctors.length}
          icon="stethoscope"
          tone="rose"
          delay={2}
        />
      </div>

      <PackageCard />

      {mayBrand && serviceShown('brand') && <HospitalBrandingPanel />}

      <QuotaGrid quota={institution.data?.quota} platform={actingAsPlatformAdmin} />

      {rows.length === 0 ? (
        <EmptyState
          icon="users"
          title="لا يوجد موظفون بعد"
          message="ابدأ بإنشاء حساب استقبال أو مدير عيادات."
        />
      ) : (
        <Panel padded={false}>
          <ul className="divide-y divide-white/5">
            {rows.map((member) => (
              <li key={member.id} className="flex flex-wrap items-center gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-white">{member.user?.name || '—'}</span>
                    <span
                      className={`badge ${ROLE_TONE[member.memberRole] || ROLE_TONE.staff}`}
                    >
                      {MEMBER_ROLE_LABELS[member.memberRole] || member.memberRole}
                    </span>
                    {!member.active && (
                      <Badge className="border-red-500/30 bg-red-500/10 text-red-300">موقوف</Badge>
                    )}
                    {Array.isArray(member.permissions) && (
                      <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-300">
                        صلاحيات مخصصة ({member.permissions.length})
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-slate-400">
                    {member.user?.username}
                    {member.user?.email ? ` · ${member.user.email}` : ''}
                    {member.servesDoctorId
                      ? ` · يعمل مع ${
                          rows.find((r) => r.userId === member.servesDoctorId)?.user?.name || '—'
                        }`
                      : ''}
                    {member.memberRole === 'nurse'
                      ? ` · ${
                          Array.isArray(member.assignedRoomIds) && member.assignedRoomIds.length
                            ? `الغرف: ${member.assignedRoomIds.map(roomName).join('، ')}`
                            : 'كل الغرف'
                        }`
                      : ''}
                  </p>
                </div>

                {mayTouch(member) && (
                  <div className="flex flex-wrap gap-2">
                    <Button variant="subtle" icon="user-cog" onClick={() => setEditing(member)}>
                      تعديل
                    </Button>
                    {member.user?.username && (
                      <Button variant="subtle" icon="key-round" onClick={() => setResetting(member)}>
                        إعادة تعيين كلمة المرور
                      </Button>
                    )}
                    {member.active ? (
                      <Button variant="ghost" className="!text-red-300" icon="user-x" onClick={() => setConfirmSuspend(member)}>
                        إيقاف
                      </Button>
                    ) : (
                      <Button variant="success" icon="user-check" onClick={() => reactivate(member)}>
                        إعادة تفعيل
                      </Button>
                    )}
                  </div>
                )}
                {member.user?.mustChangePassword && (
                  <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-200" icon="key-round">
                    كلمة مرور مؤقتة
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Modal
        open={Boolean(form)}
        onClose={() => setForm(null)}
        title="حساب موظف جديد"
        closeOnBackdrop={false}
        footer={
          <>
            <Button variant="ghost" onClick={() => setForm(null)}>
              إلغاء
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? 'جارٍ الحفظ…' : 'إنشاء الحساب'}
            </Button>
          </>
        }
      >
        {form && (
          <StaffForm
            form={form}
            set={set}
            doctors={doctors}
            rooms={roomList}
            quota={actingAsPlatformAdmin ? null : institution.data?.quota}
            roles={creatableRoles}
          />
        )}
      </Modal>

      {editing && (
        <EditAccountModal member={editing} doctors={doctors} onClose={() => setEditing(null)} onSaved={members.reload} />
      )}
      {resetting && (
        <ResetPasswordDialog member={resetting} onClose={() => setResetting(null)} onDone={members.reload} />
      )}

      <ConfirmDialog
        open={Boolean(confirmSuspend)}
        onClose={() => setConfirmSuspend(null)}
        onConfirm={suspend}
        destructive
        title="إيقاف الحساب"
        message={`سيتم إيقاف حساب ${confirmSuspend?.user?.name || ''}. لن يُحذف الحساب ولا سجله — يمكن إعادة تفعيله لاحقاً.`}
        confirmLabel="إيقاف"
      />
    </div>
  );
}

/**
 * The form. Split out so the modal body stays readable.
 *
 * Exported because the Super Admin creates staff inside any hospital from the
 * institutions screen with the same form — quota-free, so `quota` is null there.
 */
export function StaffForm({ form, set, doctors, rooms = [], quota = null, roles = CREATABLE_MEMBER_ROLES }) {
  // The central control: «صلاحيات المدير الإداري بالاختيار» may be closed, and
  // a capability whose module the package does not carry cannot be handed out.
  const { serviceShown, serviceState } = usePermissions();
  const delegation = serviceShown('coadminDelegation');
  const outOfPackage = (name) => PERMISSION_CONTROL[name] && serviceState(PERMISSION_CONTROL[name]) === 'closed';
  const isDoctor = form.memberRole === 'doctor';
  const isSecretary = form.memberRole === 'secretary';
  const isCoAdmin = form.memberRole === 'co_admin';
  const isNurse = form.memberRole === 'nurse';
  const remainingFor = (role) => quota?.byRole?.[role]?.remaining;

  const togglePermission = (name) => {
    const current = Array.isArray(form.permissions) ? form.permissions : [];
    set({
      permissions: current.includes(name)
        ? current.filter((p) => p !== name)
        : [...current, name],
    });
  };

  return (
    <div className="space-y-4">
      {/*
        Tiles rather than a dropdown. Choosing what somebody will be allowed to
        do in a hospital is the single most consequential field on this form, and
        a collapsed <select> shows one option at a time with no hint of what any
        of them means. Six tiles show every choice at once, each with a glyph and
        a one-line description of the job.
      */}
      <Field label="نوع الحساب" required>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {roles.map((role, i) => {
            const remaining = remainingFor(role);
            const full = remaining !== undefined && remaining <= 0;
            return (
              <Tile3D
                key={role}
                icon={ROLE_ICON[role]}
                label={MEMBER_ROLE_LABELS[role]}
                hint={remaining !== undefined ? `${ROLE_HINT[role]} · متبقٍ ${remaining}` : ROLE_HINT[role]}
                tone={form.memberRole === role ? ROLE_TILE_TONE[role] : 'slate'}
                pulse={form.memberRole === role}
                locked={full}
                enter
                delay={i}
                aria-pressed={form.memberRole === role}
                onClick={() => set({ memberRole: role, permissions: null, servesDoctorId: '', assignedRoomIds: [] })}
              />
            );
          })}
        </div>
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="الاسم" required>
          <Input value={form.name} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label="اسم المستخدم" required>
          <Input value={form.username} onChange={(e) => set({ username: e.target.value })} />
        </Field>
      </div>

      {/*
        Either a password or a Google address is required, never neither. Said
        here rather than discovered as a 400, because "أدخل كلمة مرور أو بريد
        Google" is a rule about how the account will be reached, not a
        validation failure.
      */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="كلمة المرور"
          hint="اتركها فارغة إذا كان الدخول عبر Google فقط"
        >
          <Input
            type="password"
            value={form.password}
            onChange={(e) => set({ password: e.target.value })}
          />
        </Field>
        <Field label="بريد Google" hint="يُفعّل الدخول بحساب Google">
          <Input
            type="email"
            dir="ltr"
            value={form.email}
            onChange={(e) => set({ email: e.target.value })}
          />
        </Field>
      </div>

      <Field label="الهاتف">
        <Input dir="ltr" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
      </Field>

      {isSecretary && (
        <Field
          label="الطبيب"
          required
          hint="السكرتير يتابع طابور طبيب واحد فقط"
        >
          <Select
            value={form.servesDoctorId}
            onChange={(e) => set({ servesDoctorId: e.target.value })}
          >
            <option value="">اختر الطبيب…</option>
            {doctors.map((d) => (
              <option key={d.userId} value={d.userId}>
                {d.user?.name || `#${d.userId}`}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {isNurse && (
        <Field label="الغرف المسندة" hint={rooms.length ? 'بدون تحديد = كل الغرف' : 'لا توجد غرف بعد — الممرض يغطي كل الغرف'}>
          <div className="flex flex-wrap gap-1.5">
            {rooms.map((room) => {
              const on = (form.assignedRoomIds || []).map(String).includes(String(room.id));
              return (
                <button
                  key={room.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    set({
                      assignedRoomIds: on
                        ? form.assignedRoomIds.filter((id) => String(id) !== String(room.id))
                        : [...(form.assignedRoomIds || []), room.id],
                    })
                  }
                  className={`min-h-[40px] rounded-xl border px-3 text-xs font-bold ${
                    on ? 'border-teal-500/50 bg-teal-500/15 text-teal-100' : 'border-white/10 text-slate-400'
                  }`}
                >
                  {room.name}
                </button>
              );
            })}
          </div>
        </Field>
      )}

      {isDoctor && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="اللقب">
            <Input value={form.title} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          <Field label="سعر الكشف">
            <Input
              type="number"
              value={form.priceExam}
              onChange={(e) => set({ priceExam: e.target.value })}
            />
          </Field>
          <Field label="سعر الاستشارة">
            <Input
              type="number"
              value={form.priceFollowup}
              onChange={(e) => set({ priceFollowup: e.target.value })}
            />
          </Field>
        </div>
      )}

      {isCoAdmin && !delegation && (
        <p className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-4 text-xs text-slate-300">
          صلاحيات المدير الإداري في باقة المنشأة حزمة ثابتة، ومش بتتحدد بالاختيار.
        </p>
      )}

      {isCoAdmin && delegation && (
        <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-4">
          <p className="mb-1 text-sm font-bold text-purple-300">الصلاحيات</p>
          <p className="mb-3 text-xs text-slate-400">
            اتركها كما هي لاستخدام صلاحيات المدير الإداري الافتراضية. أي تحديد هنا
            يستبدلها بالكامل — التحديد الفارغ يعني بلا صلاحيات.
          </p>

          {PERMISSION_GROUPS.map((group) => (
            <div key={group.label} className="mb-3 last:mb-0">
              <p className="mb-1.5 text-xs font-bold text-slate-300">{group.label}</p>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {group.permissions.map((name) => (
                  <Checkbox
                    key={name}
                    label={outOfPackage(name) ? `${PERMISSION_LABELS[name]} — مش في الباقة` : PERMISSION_LABELS[name]}
                    checked={Array.isArray(form.permissions) && form.permissions.includes(name)}
                    disabled={outOfPackage(name)}
                    onChange={() => togglePermission(name)}
                  />
                ))}
              </div>
            </div>
          ))}

          {Array.isArray(form.permissions) && (
            <button
              type="button"
              onClick={() => set({ permissions: null })}
              className="mt-2 text-xs text-cyan-400 underline"
            >
              العودة إلى الصلاحيات الافتراضية
            </button>
          )}
        </div>
      )}

      <p className="flex items-start gap-2 text-xs text-slate-400">
        <Icon name="shield-check" size={14} className="mt-0.5 shrink-0 text-emerald-400" />
        كل الصلاحيات تُفحص على الخادم في كل طلب — إخفاء زر ليس حماية.
      </p>
    </div>
  );
}

export default HospitalStaff;
